import { useState } from "react";
import style from "./participants-full.module.css";
import defaultAvatar from "../../images/196m.png";
import { TEvent, TProfile } from "../../types";
import { pluralizeRu } from "../../utils/pluralize";
import { useAppSelector } from "../../services/store/hooks";
import ParticipantFeedbackModal from "./participant-feedback-modal";
import ProfilePreviewModal from "../profile-preview-modal/profile-preview-modal";
import { useSendProfileActionMutation } from "../../services/store/api/profiles";
import { useAcceptRequestMutation } from "../../services/store/api/matchApi";
import ConfirmModal from "../confirm-modal/confirm-modal";
interface Props {
    event: TEvent
}
const ParticipantsFull = ({ event }: Props) => {
    const currentProfile = useAppSelector((state) => state.user.profile);
    const [selectedFeedbackParticipant, setSelectedFeedbackParticipant] = useState<TProfile | null>(null);
    const [selectedProfile, setSelectedProfile] = useState<TProfile | null>(null);
    const [participantToOwn, setParticipantToOwn] = useState<TProfile | null>(null);
    const [processingProfileId, setProcessingProfileId] = useState<number | null>(null);
    const [sendProfileAction] = useSendProfileActionMutation();
    const [acceptRequest] = useAcceptRequestMutation();
    // Расчет свободных мест
    const participantsCount = event.profiles ? event.profiles.length : 0;
    const freePlaces = Math.max(0, event.places - participantsCount);
    const freePlacesWord = pluralizeRu(freePlaces, "место", "места", "мест");

    const lastParticipants = event.profiles ?? [];
    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        const target = e.currentTarget as HTMLImageElement;
        target.src = defaultAvatar;
    };

    const isFinished = event.is_finished;
    const canLeaveFeedback = Boolean(event.can_leave_feedback && currentProfile?.id && isFinished);

    const handleOwnClick = (profile: TProfile) => {
        if (!currentProfile || processingProfileId) return;

        setParticipantToOwn(profile);
    };

    const handleConfirmOwn = async () => {
        if (!currentProfile || !participantToOwn || processingProfileId) return;

        const profile = participantToOwn;

        setProcessingProfileId(profile.id);

        try {
            if (profile.match_status === "incoming_pending" && profile.match_request_id) {
                await acceptRequest(profile.match_request_id).unwrap();
            } else if (!profile.match_status || profile.match_status === "none") {
                await sendProfileAction({ profileId: profile.id, actionType: "approve" }).unwrap();
            }
            setParticipantToOwn(null);
        } finally {
            setProcessingProfileId(null);
        }
    };

    const isOwnConfirmLoading = Boolean(participantToOwn && processingProfileId === participantToOwn.id);

    return (
        <>
            <div className={style.containerFull}>
                {participantsCount > 0 && (
                    <>
                        <h3 className={style.h3}>Уже идут <span className="my-text-primary">({participantsCount})</span></h3>
                        <div className={style.participants}>
                            <div className={style.avatars}>
                                {lastParticipants.map((profile, index) => {
                                    const canReportCurrentParticipant = Boolean(
                                        canLeaveFeedback && currentProfile?.id !== profile.id,
                                    );

                                    return (
                                        <div key={profile.id || index} className={style.participant}>
                                            <button
                                                type="button"
                                                className={style.avatarWrapper}
                                                title={profile.name || 'Участник'}
                                                onClick={() => setSelectedProfile(profile)}
                                                aria-label={`Показать анкету ${profile.name || 'участника'}`}
                                            >
                                                <img
                                                    src={profile.photos?.[0]?.image_urls?.small || profile.photos?.[0]?.url || defaultAvatar}
                                                    alt="Аватар"
                                                    className={style.avatar}
                                                    onError={handleImageError}
                                                />
                                            </button>
                                            <div className={style.participantInfo}>
                                                <div className={style.nameRow}>
                                                    <button
                                                        type="button"
                                                        className={style.nameButton}
                                                        onClick={() => setSelectedProfile(profile)}
                                                    >
                                                        {profile.name}
                                                    </button>

                                                    {profile.match_status === "matched" && (
                                                        <span className={style.ownBadge}>Свой</span>
                                                    )}

                                                    {currentProfile?.id !== profile.id
                                                        && (profile.match_status === "incoming_pending" || !profile.match_status || profile.match_status === "none")
                                                        && (
                                                            <button
                                                                type="button"
                                                                className={style.ownButton}
                                                                disabled={processingProfileId === profile.id}
                                                                onClick={() => handleOwnClick(profile)}
                                                            >
                                                                {processingProfileId === profile.id ? "..." : "Свой!"}
                                                            </button>
                                                        )}

                                                    {canReportCurrentParticipant && (
                                                        <button
                                                            type="button"
                                                            className={style.reportButton}
                                                            onClick={() => setSelectedFeedbackParticipant(profile)}
                                                        >
                                                            Пожаловаться
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                            </div>
                        </div>
                    </>
                )}

                {!isFinished && (
                    <div className={style.freePlaces}>
                        {freePlaces > 0 && (<>Осталось  <span>{freePlaces}</span> {freePlacesWord}</>)}
                        {freePlaces < 1 && (<>Мест нет</>)}
                    </div>
                )}
            </div>

            <ParticipantFeedbackModal
                eventId={event.id}
                participant={selectedFeedbackParticipant}
                isOpen={Boolean(selectedFeedbackParticipant)}
                onClose={() => setSelectedFeedbackParticipant(null)}
            />

            <ProfilePreviewModal
                profile={selectedProfile}
                isOpen={Boolean(selectedProfile)}
                onClose={() => setSelectedProfile(null)}
            />

            <ConfirmModal
                isOpen={Boolean(participantToOwn)}
                title="Добавить в Свои?"
                description={participantToOwn?.match_status === "incoming_pending"
                    ? `${participantToOwn.name} уже отправил(а) вам заявку. Принять ее и создать мэтч?`
                    : participantToOwn
                        ? `Отправить заявку пользователю ${participantToOwn.name}?`
                        : undefined}
                confirmText="Свой!"
                cancelText="Назад"
                loadingText="Отправка..."
                isLoading={isOwnConfirmLoading}
                onCancel={() => {
                    if (!isOwnConfirmLoading) {
                        setParticipantToOwn(null);
                    }
                }}
                onConfirm={handleConfirmOwn}
            />
        </>
    );
}
export default ParticipantsFull;
